import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, UseFormReturn } from "react-hook-form";
import {
  CompanyOpportunityFormSchema,
  companyOpportunityFormSchema,
} from "./schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { Redo2, Sparkles, Undo2 } from "lucide-react";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { CurrencyInput, Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { MultiToggleField } from "@/components/ui/toggle-button";

import formatBudgetRange from "@/utils/formatBudgetRange";

import { useCompanyOpportunityQuery } from "@/queries/company/opportunities";
import { useOpportunitiesSkillsQuery } from "@/queries/opportunity/skills-list";
import { useOpportunitiesDraftJobDescriptionsQuery } from "@/queries/opportunity/draft-job-description-list";

import {
  useCompanyOpportunityAIDescriptionMutation,
  useCompanyOpportunityDraftMutation,
} from "./mutations";

import { CompanyJobOpening, DraftJobOpeningDescription } from "@/types";
import { AdminJobDetails } from "@/queries/admin/job-details";

interface BaseProps {
  opening?: CompanyJobOpening | AdminJobDetails;
  companyId?: string;
  defaultTitle?: string;
  modal?: boolean;
  includeMaskedJobDescription?: boolean;
  redirectAfterCreation?: false;
  onCancel?: () => void;
  onSuccess?: () => void;
}

type Props = BaseProps &
  (
    | {
        includeSteps: true;
        startingStep: number;
        stepTotal: number;
      }
    | {
        includeSteps?: false;
        startingStep?: never;
        stepTotal?: never;
      }
  );

const CompanyFullTimeAIForm = ({
  opening,
  companyId,
  defaultTitle,
  includeSteps,
  startingStep,
  stepTotal,
  modal,
  redirectAfterCreation,
  onCancel,
  onSuccess: successCallback,
}: Props) => {
  const [openingId, setOpeningId] = useState<string | undefined>(opening?.id);
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  const { opportunityData } = useCompanyOpportunityQuery(openingId);
  const { draftJobDescriptions } =
    useOpportunitiesDraftJobDescriptionsQuery(openingId);

  const navigate = useNavigate();

  const {
    mutate,
    isPending: mutationLoading,
    isError: mutationError,
  } = useCompanyOpportunityDraftMutation(openingId, companyId);
  const {
    mutate: generateDescription,
    // mutationLoading: isGeneratingDescription,
  } = useCompanyOpportunityAIDescriptionMutation();

  const form = useForm<CompanyOpportunityFormSchema>({
    resolver: async (data, context, opts) => {
      if (opening && !data.jobDescription) {
        data.jobDescription = new File([], "");
      }

      return zodResolver(companyOpportunityFormSchema)(data, context, opts);
    },
    defaultValues: {
      jobTitle: opening?.role ?? defaultTitle ?? "",
      budgetMinCents: opening?.budgetMinCents,
      budgetMaxCents: opening?.budgetMaxCents,
      budgetNotes: opening?.budgetNotes,
      idealStartDate: opening?.desiredStartDate
        ? new Date(opening?.desiredStartDate ?? "")
        : undefined,
      briefDescription: opening?.shortDescription ?? "",
      goals: opening?.goals ?? "",
      matchCriteria: [
        opening?.matchCriteria1,
        opening?.matchCriteria2,
        opening?.matchCriteria3,
      ].filter((v): v is string => Boolean(v)),
      additionalInformation: opening?.noteForVenturous ?? "",
    },
  });

  useEffect(() => {
    if (opportunityData?.shortDescription) {
      form.setValue("briefDescription", opportunityData?.shortDescription);
    }
  }, [form, opportunityData]);

  function onSubmit(data: CompanyOpportunityFormSchema) {
    if (mutationLoading) return;

    const mode = !openingId ? "create" : opening ? "update" : "publish";
    mutate({
      data: data,
      mode,
      onSuccess: (data: CompanyJobOpening) => {
        if (mode === "publish" || mode === "update") {
          successCallback?.();
          if (redirectAfterCreation) {
            navigate("/opening/" + data.id);
          }
        } else if (mode === "create") {
          generateDescription({
            openingId: data.id,
            description: "",
            message: "",
            onToken: (_, full) => {
              form.setValue("briefDescription", full);
            },
            onSuccess: (data) => {
              form.setValue("briefDescription", data.description);
            },
          });
        }

        setOpeningId(data.id);
      },
    });
  }

  function onBack() {
    if (step > 1) {
      setStep((step) => (step - 1) as 1 | 2 | 3 | 4);
    } else {
      onCancel?.();
    }
  }

  async function onNext() {
    if (step === 4) {
      return form.handleSubmit(onSubmit)();
    }

    let fields: (keyof CompanyOpportunityFormSchema)[];
    if (step === 1) {
      fields = ["jobTitle", "goals", "idealStartDate"];
    } else if (step === 2) {
      fields = ["matchCriteria"];
    } else {
      // step === 3
      fields = ["budgetMinCents", "budgetMaxCents", "budgetNotes"];
    }

    const valid = await form.trigger(fields);
    if (!valid) return;

    // Save draft if we've collected the budget
    if (step === 3 && !openingId) {
      onSubmit(form.getValues());
    }
    setStep((step) => (step + 1) as 1 | 2 | 3 | 4);
  }

  return (
    <Form {...form}>
      <form className="space-y-8 w-full">
        {mutationError && (
          <p className="text-center w-full border border-destructive rounded-md text-destructive p-2 bg-destructive/10">
            Something went wrong when {opening ? "updating" : "creating"} the
            job opening, please try again later.
          </p>
        )}
        {step === 1 ? (
          <Scope
            form={form}
            opening={opening}
            modal={modal}
            step={includeSteps ? startingStep + step - 1 : undefined}
            stepTotal={stepTotal}
          />
        ) : step === 2 ? (
          <Skills
            form={form}
            opening={opening}
            step={includeSteps ? startingStep + step - 1 : undefined}
            stepTotal={stepTotal}
          />
        ) : step === 3 ? (
          <Budget
            form={form}
            opening={opening}
            step={includeSteps ? startingStep + step - 1 : undefined}
            stepTotal={stepTotal}
          />
        ) : (
          <JobDescription
            form={form}
            opening={opening}
            openingId={openingId}
            draftJobDescriptions={draftJobDescriptions}
            step={includeSteps ? startingStep + step - 1 : undefined}
            stepTotal={stepTotal}
            loading={mutationLoading}
          />
        )}
        <div className="h-[1px] bg-[#E4E7EC] w-full" />
        <div className="!mb-1 flex justify-between">
          {step || onCancel ? (
            <Button
              variant="stroke"
              className="mt-2"
              onClick={(e) => {
                e.preventDefault();
                void onBack();
              }}
            >
              Back
            </Button>
          ) : (
            <div />
          )}
          <Button
            variant="success"
            type="submit"
            className="mt-2"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={(e) => {
              e.preventDefault();
              void onNext();
            }}
          >
            {step === 4 ? "Submit For Review" : "Continue"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

const Scope = (props: {
  form: UseFormReturn<CompanyOpportunityFormSchema>;
  opening: CompanyJobOpening | AdminJobDetails | undefined;
  step?: number;
  stepTotal?: number;
  modal?: boolean;
}) => {
  return (
    <>
      <div className="flex flex-col gap-2 self-stretch items-start">
        {props.step && props.stepTotal && (
          <p className="text-sm font-medium text-inline-link self-start">
            Step {props.step} of {props.stepTotal}
          </p>
        )}
        <div className="flex flex-col gap-1 self-stretch items-start">
          <h1 className="text-3xl text-[#2E2E38] font-bold">
            Tell us about your organization’s needs
          </h1>
          <p className="text-base text-brand-neutral-black-8">
            We’ll find a full-time leader to join your team.
          </p>
        </div>
      </div>
      <FormField
        control={props.form.control}
        name="jobTitle"
        render={({ field }) => (
          <FormItem>
            <FormLabel variant="subtle" required>
              Job title
            </FormLabel>
            <FormControl>
              <Input {...field} placeholder="CEO" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={props.form.control}
        name="goals"
        render={({ field }) => (
          <FormItem>
            <FormLabel variant="subtle" required>
              Summarize what you’re looking for
            </FormLabel>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={props.form.control}
        name="idealStartDate"
        render={({ field }) => (
          <FormItem>
            <FormLabel variant="subtle" required>
              Ideal start date
            </FormLabel>
            <FormControl>
              <DatePicker
                value={field.value}
                onChange={field.onChange}
                modal={props.modal}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};

const Skills = (props: {
  form: UseFormReturn<CompanyOpportunityFormSchema>;
  opening: CompanyJobOpening | AdminJobDetails | undefined;
  step?: number;
  stepTotal?: number;
}) => {
  const [title, goals] = props.form.watch(["jobTitle", "goals"]);
  const { skills, skillsLoading } = useOpportunitiesSkillsQuery({
    title,
    goals,
  });

  if (skillsLoading) {
    return <LoadingScreen text="Fetching skill suggestions" />;
  }

  return (
    <>
      <div className="flex flex-col gap-2 self-stretch items-start">
        {props.step && props.stepTotal && (
          <p className="text-sm font-medium text-inline-link self-start">
            Step {props.step} of {props.stepTotal}
          </p>
        )}
        <div className="flex flex-col gap-1 self-stretch items-start">
          <h1 className="text-3xl text-[#2E2E38] font-bold">
            What skills or experiences are most critical?
          </h1>
          <p className="text-base text-brand-neutral-black-8">
            The more details, the better to match you with a leader best suited
            to the work.
          </p>
        </div>
      </div>
      <FormField
        control={props.form.control}
        name="matchCriteria"
        render={({ field }) => (
          <FormItem>
            <FormLabel variant="subtle" className="text-sm" required>
              Must-have skills or experience (choose 3)
            </FormLabel>
            <FormControl>
              <MultiToggleField
                {...field}
                options={skills ?? []}
                maxSelected={3}
                allowOther
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};

const Budget = (props: {
  form: UseFormReturn<CompanyOpportunityFormSchema>;
  opening: CompanyJobOpening | AdminJobDetails | undefined;
  step?: number;
  stepTotal?: number;
}) => {
  const [candidateFeeString, setCandidateFeeString] = useState("");
  const [venturousFeeString, setVenturousFeeString] = useState("");

  const generateFeeStrings = useCallback(() => {
    const [budgetMinCents, budgetMaxCents] = props.form.getValues([
      "budgetMinCents",
      "budgetMaxCents",
    ]);

    if (!budgetMinCents || !budgetMaxCents) {
      setCandidateFeeString("");
      setVenturousFeeString("");

      return;
    }

    setCandidateFeeString(
      formatBudgetRange(budgetMinCents, budgetMaxCents) ?? "",
    );
    setVenturousFeeString(
      formatBudgetRange(budgetMinCents * 0.15, budgetMaxCents * 0.15) ?? "",
    );
  }, [props.form, setCandidateFeeString, setVenturousFeeString]);

  const min = props.form.watch("budgetMinCents");
  const max = props.form.watch("budgetMaxCents");

  const minError = props.form.formState.errors.budgetMinCents?.message;
  const maxError = props.form.formState.errors.budgetMaxCents?.message;

  const budgetError = maxError ?? minError;

  const canCalculate = Boolean(min) && Boolean(max) && min <= max;

  return (
    <>
      <div className="flex flex-col gap-2 self-stretch items-start">
        {props.step && props.stepTotal && (
          <p className="text-sm font-medium text-inline-link self-start">
            Step {props.step} of {props.stepTotal}
          </p>
        )}
        <div className="flex flex-col gap-1 self-stretch items-start">
          <h1 className="text-3xl text-[#2E2E38] font-bold">
            Let’s talk budget
          </h1>
          <p className="text-base text-brand-neutral-black-8">
            The more details, the better to match you with a leader best suited
            to the work.
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-12 self-stretch">
        <div className="flex flex-col gap-1 self-stretch">
          <FormLabel variant="subtle" required>
            Salary budget
          </FormLabel>
          <div className="flex flex-row gap-6 self-stretch items-center">
            <div className="flex flex-row gap-1 self-stretch items-center">
              <FormField
                control={props.form.control}
                name="budgetMinCents"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <CurrencyInput
                        valueCents={field.value}
                        onValueCentsChange={field.onChange}
                        minCents={0}
                        placeholder="100,000"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <span className="select-none">-</span>
              <FormField
                control={props.form.control}
                name="budgetMaxCents"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <CurrencyInput
                        valueCents={field.value}
                        onValueCentsChange={field.onChange}
                        minCents={0}
                        placeholder="150,000"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
          {budgetError ? (
            <p className="text-sm font-medium text-destructive">
              {String(budgetError)}
            </p>
          ) : null}
        </div>
        <div className="w-full h-[1px] bg-[#E3E3E9]" />
        <div className="flex flex-row gap-6 self-stretch">
          <div className="py-8 flex-1 flex flex-col gap-6">
            <div className="flex flex-col gap-1 self-stretch">
              <h2 className="text-xl text-[#2E2E38] font-bold">
                Estimated charges
              </h2>
              <p className="text-base text-brand-neutral-black-8">
                Based on the salary range provided. Final charges will be based
                on payment terms agreed upon with candidate.
              </p>
            </div>
            <Button
              variant="stroke"
              type="button"
              onClick={generateFeeStrings}
              disabled={!canCalculate}
            >
              Calculate
            </Button>
          </div>
          <div className="p-8 flex-1 flex flex-col gap-10 border-2 border-brand-primary-400 rounded-2xl">
            <div className="flex flex-col gap-4 self-stretch">
              <div className="flex flex-col gap-1 self-stretch">
                <p className="text-base text-[#2E2E38] font-medium">
                  You’ll pay the candidate:
                </p>
                {candidateFeeString ? (
                  <p className="text-3xl text-brand-primary-400 font-bold">
                    {candidateFeeString}
                    <span className="text-base font-medium">/yr</span>
                  </p>
                ) : (
                  <p className="text-3xl text-brand-neutral-black-8 font-bold">
                    --
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1 self-stretch">
                <p className="text-base text-[#2E2E38] font-medium">
                  You’ll pay Venturous:
                </p>
                <div className="flex flex-col gap-1">
                  {venturousFeeString ? (
                    <p className="text-3xl text-brand-primary-400 font-bold">
                      {venturousFeeString}
                    </p>
                  ) : (
                    <p className="text-3xl text-brand-neutral-black-8 font-bold">
                      --
                    </p>
                  )}

                  <span className="text-base text-brand-neutral-black-8 font-normal">
                    (15% of total candidate fees)
                  </span>
                </div>
              </div>
            </div>
            <p className="text-sm text-brand-neutral-black-8">
              * Tax not included
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

const JobDescription = (props: {
  form: UseFormReturn<CompanyOpportunityFormSchema>;
  opening: CompanyJobOpening | AdminJobDetails | undefined;
  openingId?: string;
  draftJobDescriptions?: DraftJobOpeningDescription[] | null;
  step?: number;
  stepTotal?: number;
  loading: boolean;
}) => {
  const [message, setMessage] = useState("");
  const [currentDraftIdx, setCurrentDraftIdx] = useState(0);

  const { mutate: generateDescription, isPending: isGeneratingDescription } =
    useCompanyOpportunityAIDescriptionMutation();

  useEffect(() => {
    if (!props.draftJobDescriptions) {
      return;
    } else {
      setDraft(props.draftJobDescriptions.length - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.draftJobDescriptions]);

  const setDraft = (idx: number) => {
    const target = props.draftJobDescriptions?.[idx];
    if (!target) {
      return;
    }

    setCurrentDraftIdx(idx);
    props.form.setValue("briefDescription", target.description);
  };

  const sendMessage = () => {
    if (!props.openingId) {
      return;
    }

    generateDescription({
      openingId: props.openingId,
      description: props.form.getValues("briefDescription"),
      message,
      onToken: (_, full) => {
        props.form.setValue("briefDescription", full);
      },
      onSuccess: () => setMessage(""),
    });
  };

  return (
    <>
      <div className="flex flex-col gap-2 self-stretch items-start">
        {props.step && props.stepTotal && (
          <p className="text-sm font-medium text-inline-link self-start">
            Step {props.step} of {props.stepTotal}
          </p>
        )}
        <div className="flex flex-col gap-1 self-stretch items-start">
          <div className="flex flex-row gap-3">
            <h1 className="text-3xl text-[#2E2E38] font-bold">
              Review your job description
            </h1>
            <div className="p-1.5 flex flex-row gap-1 bg-[linear-gradient(117deg,#1D5249_32.26%,#86B860_104.02%)] rounded">
              <Sparkles stroke="white" />
              <p className="text-base text-white">
                <span className="font-medium">Venturous</span>AI
              </p>
            </div>
          </div>
          <p className="text-base text-brand-neutral-black-8">
            Please review or modify your job description to ensure it reflects
            your company’s needs.
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <FormField
          control={props.form.control}
          name="briefDescription"
          render={({ field }) => (
            <FormItem>
              <FormLabel
                subheading="You can edit the job description directly, or type feedback to generate a new response"
                variant="subtle"
                required
              >
                Job description:
              </FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Covering day-to-day financial operations, financial planning, and financial analysis"
                  className="min-h-32 md:h-60"
                  disabled={props.loading || isGeneratingDescription}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-row gap-2 items-center">
          <Input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={props.loading || isGeneratingDescription}
            placeholder="Type your feedback to to generate a new version..."
          />
          <Button
            variant="stroke"
            type="submit"
            disabled={props.loading || isGeneratingDescription}
            onClick={(e) => {
              e.preventDefault();
              sendMessage();
            }}
          >
            Update
          </Button>
          <Button
            variant="stroke"
            disabled={!currentDraftIdx}
            onClick={(e) => {
              e.preventDefault();
              setDraft(currentDraftIdx - 1);
            }}
          >
            <Undo2 />
          </Button>
          <Button
            variant="stroke"
            disabled={
              !props.draftJobDescriptions ||
              props.draftJobDescriptions.length <= currentDraftIdx + 1
            }
            onClick={(e) => {
              e.preventDefault();
              setDraft(currentDraftIdx + 1);
            }}
          >
            <Redo2 />
          </Button>
        </div>
      </div>
    </>
  );
};

const LoadingScreen = (props: { text: string }) => {
  return (
    <div className="flex flex-col gap-1 items-center">
      <div className="my-20">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="82"
          height="82"
          viewBox="0 0 82 82"
          fill="none"
        >
          <path
            d="M37.6415 9.61357C37.7879 8.82979 38.2038 8.1219 38.8171 7.61249C39.4305 7.10307 40.2027 6.82422 41 6.82422C41.7974 6.82422 42.5696 7.10307 43.183 7.61249C43.7963 8.1219 44.2122 8.82979 44.3586 9.61357L47.9495 28.6034C48.2046 29.9535 48.8607 31.1953 49.8322 32.1669C50.8038 33.1384 52.0456 33.7945 53.3957 34.0496L72.3855 37.6405C73.1693 37.7869 73.8772 38.2028 74.3866 38.8162C74.896 39.4295 75.1749 40.2017 75.1749 40.9991C75.1749 41.7964 74.896 42.5686 74.3866 43.182C73.8772 43.7954 73.1693 44.2113 72.3855 44.3577L53.3957 47.9486C52.0456 48.2036 50.8038 48.8597 49.8322 49.8313C48.8607 50.8028 48.2046 52.0446 47.9495 53.3947L44.3586 72.3846C44.2122 73.1683 43.7963 73.8762 43.183 74.3856C42.5696 74.8951 41.7974 75.1739 41 75.1739C40.2027 75.1739 39.4305 74.8951 38.8171 74.3856C38.2038 73.8762 37.7879 73.1683 37.6415 72.3846L34.0505 53.3947C33.7955 52.0446 33.1394 50.8028 32.1679 49.8313C31.1963 48.8597 29.9545 48.2036 28.6044 47.9486L9.61454 44.3577C8.83077 44.2113 8.12288 43.7954 7.61346 43.182C7.10405 42.5686 6.8252 41.7964 6.8252 40.9991C6.8252 40.2017 7.10405 39.4295 7.61346 38.8162C8.12288 38.2028 8.83077 37.7869 9.61454 37.6405L28.6044 34.0496C29.9545 33.7945 31.1963 33.1384 32.1679 32.1669C33.1394 31.1953 33.7955 29.9535 34.0505 28.6034L37.6415 9.61357Z"
            stroke="url(#paint0_linear_207_7970)"
            strokeWidth="5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M68.333 4.33398C69.7137 4.33398 70.833 5.45327 70.833 6.83398V11.166H75.167C76.5476 11.1662 77.667 12.2854 77.667 13.666C77.667 15.0466 76.5476 16.1658 75.167 16.166H70.833V20.501C70.8328 21.8815 69.7136 23.001 68.333 23.001C66.9525 23.0008 65.8332 21.8815 65.833 20.501V16.166H61.5C60.1193 16.166 59 15.0467 59 13.666C59 12.2853 60.1193 11.166 61.5 11.166H65.833V6.83398C65.833 5.45335 66.9524 4.33412 68.333 4.33398Z"
            fill="url(#paint1_linear_207_7970)"
          />
          <path
            d="M13.6668 75.1667C17.4408 75.1667 20.5002 72.1073 20.5002 68.3333C20.5002 64.5594 17.4408 61.5 13.6668 61.5C9.89288 61.5 6.8335 64.5594 6.8335 68.3333C6.8335 72.1073 9.89288 75.1667 13.6668 75.1667Z"
            stroke="url(#paint2_linear_207_7970)"
            strokeWidth="5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <defs>
            <linearGradient
              id="paint0_linear_207_7970"
              x1="26.994"
              y1="19.415"
              x2="91.0669"
              y2="52.3675"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0.083237" stopColor="#1D5249" />
              <stop offset="1" stopColor="#86B860" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_207_7970"
              x1="64.5083"
              y1="7.77264"
              x2="82.0073"
              y2="16.7723"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0.083237" stopColor="#1D5249" />
              <stop offset="1" stopColor="#86B860" />
            </linearGradient>
            <linearGradient
              id="paint2_linear_207_7970"
              x1="10.8663"
              y1="64.0175"
              x2="23.6778"
              y2="70.6065"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0.083237" stopColor="#1D5249" />
              <stop offset="1" stopColor="#86B860" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <p className="text-2xl text-[#252525] font-bold">{props.text}</p>
    </div>
  );
};

export default CompanyFullTimeAIForm;
